// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box } from '@material-ui/core';
import {
  useAlert,
  useHistoryState,
  useIfMounted,
  useOnChange,
  useOnMount,
} from '../../../../../common/hooks';
import { AlertSeverity } from '../../../../../common/providers';
import { AppRoute } from '../../../../app';
import { EmptyState, LoadingWrapper, Panel } from '../../../../../common/components';
import { selectPendingActions } from '../../../../../common/state/pending';
import { selectCompanies } from '../../../state/companySelectors';
import { createCompany, fetchCompanies, searchCompanies } from '../../../state/companyActions';
import CompanyList from './partials/CompanyList';

const useStyles = makeStyles(() => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    flex: '1',
    height: '100%',
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
}));

function CompaniesPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [filterParams, setFilterParams] = useHistoryState('filterParams', {
    onlyActive: true,
    withContracts: false,
    searchTerm: '',
  });
  const [sortParams, setSortParams] = useHistoryState('sortParams', {
    orderBy: 'name',
    sortOrder: 'asc',
  });
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const companies = useSelector(selectCompanies);
  const pendingActions = useSelector(selectPendingActions);
  const history = useHistory();
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  useOnMount(() => {
    async function handleFetchData() {
      try {
        if (!companies) {
          setLoading(true);
        }
        if (!filterParams.searchTerm) {
          await dispatch(fetchCompanies(filterParams, sortParams));
        } else {
          await dispatch(searchCompanies(filterParams));
        }
      } catch (e) {
        showAlert({
          title: t('company.companiesFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    }

    handleFetchData();
  });

  const handleFetchCompanies = useCallback(
    async page => {
      try {
        await dispatch(fetchCompanies(filterParams, sortParams, page));
      } catch (e) {
        showAlert({
          title: t('company.companiesFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, filterParams, showAlert, sortParams, t]
  );

  const handleCreateCompany = useCallback(
    async companyData => {
      try {
        const { id } = await dispatch(createCompany(companyData));
        await dispatch(fetchCompanies(filterParams, sortParams, companies?.page));
        ifMounted(() =>
          history.push(AppRoute.CompanyDetails.replace(':companyId', id), {
            from: history.location,
          })
        );
        showAlert({
          title: t('common.success'),
          message: t('company.companyCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('company.companyCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, companies, filterParams, sortParams, history, ifMounted, showAlert, t]
  );

  const handleFilterChange = useCallback(
    params => {
      setFilterParams({
        ...filterParams,
        ...params,
      });
    },
    [filterParams, setFilterParams]
  );

  const handleSortChange = useCallback(
    params => {
      setSortParams({
        ...sortParams,
        ...params,
      });
    },
    [sortParams, setSortParams]
  );

  const handleSearchTermChange = filters => {
    dispatch(searchCompanies(filters));
  };

  const handleCompanySearch = _.debounce(params => {
    const filters = {
      ...params,
    };
    setFilterParams(filters);
  }, 1000);

  const handleSelectCompany = useCallback(
    params => {
      history.push(AppRoute.CompanyDetails.replace(':companyId', params.row.id), {
        from: history.location,
      });
    },
    [history]
  );

  useOnChange(filterParams, () => {
    if (!filterParams.searchTerm) {
      handleFetchCompanies(1);
    } else {
      handleSearchTermChange(filterParams);
    }
  });

  useOnChange(sortParams, () => {
    handleFetchCompanies(companies?.page);
  });

  return (
    <Box id="Companies-container" className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel id="Companies-panel" title={t('company.companies')} className={classes.panel}>
          {!companies ? (
            <EmptyState variant="unavailable" message={t('company.companiesNotAvailable')} />
          ) : (
            <CompanyList
              data={companies}
              filterParams={filterParams}
              sortParams={sortParams}
              onFetchData={handleFetchCompanies}
              onSelectCompany={handleSelectCompany}
              onCreateCompany={handleCreateCompany}
              onFilterChange={handleFilterChange}
              onSortChange={handleSortChange}
              onCompanySearch={handleCompanySearch}
              actionsDisabled={!_.isEmpty(pendingActions)}
            />
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default CompaniesPage;
